// GlobalFilter.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Filter, X, ChevronDown } from 'lucide-react';
import { getAdminStatistics } from '../../../../Component/Servicefile/ReportAnalysisService';

const GlobalFilter = ({ 
    onFilterChange, 
    onLoadingChange, 
    isMobile, 
    activeTab 
}) => {
    const [selectedFilter, setSelectedFilter] = useState('today');
    const [customFromDate, setCustomFromDate] = useState('');
    const [customToDate, setCustomToDate] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [showCustomRange, setShowCustomRange] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const dropdownRef = useRef(null);

    // Preset date options
    const dateOptions = [
        { id: 'today', label: 'Today' },
        { id: 'yesterday', label: 'Yesterday' },
        { id: 'week', label: 'This Week' },
        { id: 'month', label: 'This Month' },
        { id: 'lastMonth', label: 'Last Month' },
        { id: 'custom', label: 'Custom Range' }
    ];

    // Get date range based on selected filter
    const getDateRange = (filterId) => {
        const today = new Date();
        let fromDate = new Date();
        let toDate = new Date();

        switch (filterId) {
            case 'today':
                fromDate = new Date(today);
                toDate = new Date(today);
                break;
            case 'yesterday':
                fromDate = new Date(today);
                fromDate.setDate(fromDate.getDate() - 1);
                toDate = new Date(fromDate);
                break;
            case 'week':
                fromDate = new Date(today);
                fromDate.setDate(fromDate.getDate() - fromDate.getDay());
                toDate = new Date(today);
                break;
            case 'month':
                fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
                toDate = new Date(today);
                break;
            case 'lastMonth':
                fromDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                toDate = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            default:
                return null;
        }

        return {
            from: formatDate(fromDate),
            to: formatDate(toDate)
        };
    };

    // Format date to YYYY-MM-DD
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Format date for display (DD-MM-YYYY)
    const formatDisplayDate = (dateString) => {
        if (!dateString) return '';
        const parts = dateString.split('-');
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    };

    // Fetch data with date filter
    const fetchFilteredData = async (fromDate, toDate) => {
        setIsLoading(true);
        onLoadingChange(true);

        try {
            const vendorId = localStorage.getItem("vendorId");
            const response = await getAdminStatistics(vendorId, fromDate, toDate);
            
            if (response && response.message !== "No orders found for selected date range") {
                onFilterChange(response);
            } else {
                // No data found
                onFilterChange({ 
                    message: "No orders found for selected date range",
                    isEmpty: true 
                });
            }
        } catch (error) {
            console.error('Error fetching filtered data:', error);
            onFilterChange({ error: error.message });
        } finally {
            setIsLoading(false);
            onLoadingChange(false);
        }
    };

    // Handle filter selection
    const handleFilterSelect = (filterId) => {
        setSelectedFilter(filterId);
        setShowDropdown(false);

        if (filterId === 'custom') {
            setShowCustomRange(true);
            return;
        }

        setShowCustomRange(false);
        const dateRange = getDateRange(filterId);
        if (dateRange) {
            fetchFilteredData(dateRange.from, dateRange.to);
        }
    };

    // Handle custom range apply
    const handleCustomRangeApply = () => {
        if (!customFromDate || !customToDate) {
            alert('Please select both From and To dates');
            return;
        }

        if (customFromDate > customToDate) {
            alert('From Date cannot be later than To Date');
            return;
        }

        setShowCustomRange(false);
        setSelectedFilter('custom');
        fetchFilteredData(customFromDate, customToDate);
    };

    // Handle clear filter
    const handleClearFilter = () => {
        setSelectedFilter('today');
        setCustomFromDate('');
        setCustomToDate('');
        setShowCustomRange(false);
        setShowDropdown(false);
        
        // Fetch today's data
        const dateRange = getDateRange('today');
        if (dateRange) {
            fetchFilteredData(dateRange.from, dateRange.to);
        }
    };

    // Get display text for selected filter
    const getDisplayText = () => {
        if (selectedFilter === 'custom' && customFromDate && customToDate) {
            return `${formatDisplayDate(customFromDate)} - ${formatDisplayDate(customToDate)}`;
        }
        const option = dateOptions.find(opt => opt.id === selectedFilter);
        return option ? option.label : 'Select Date';
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Load initial data on mount
    useEffect(() => {
        const dateRange = getDateRange('today');
        if (dateRange) {
            fetchFilteredData(dateRange.from, dateRange.to);
        }
    }, []);

    return (
        <div className="global-filter-container" ref={dropdownRef}>
            <div className="filter-wrapper">
                {/* Date Filter Button */}
                <button
                    className="filter-trigger-btn"
                    onClick={() => setShowDropdown(!showDropdown)}
                    disabled={isLoading}
                >
                    <Calendar size={16} />
                    <span className="filter-label">{getDisplayText()}</span>
                    <ChevronDown size={14} className={`chevron-icon ${showDropdown ? 'rotate' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {showDropdown && (
                    <div className="filter-dropdown-menu">
                        {dateOptions.map((option) => (
                            <button
                                key={option.id}
                                className={` px-3 py-2 text-xs hover:bg-gray-100 cursor-pointer dropdown-item ${selectedFilter === option.id ? 'active' : ''}`}
                                onClick={() => handleFilterSelect(option.id)}
                            >
                                {option.label}
                            </button>
                        ))}
                        
                        <div className="dropdown-divider"></div>
                        
                        <button
                            className="px-3 py-2 text-xs hover:bg-gray-100 cursor-pointer dropdown-item clear-btn"
                            onClick={handleClearFilter}
                        >
                            Clear Filter
                        </button>
                    </div>
                )}

                {/* Export Button */}
                <button className="export-btn">
                    <span>Export</span>
                </button>
            </div>

            {/* Custom Range Modal */}
            {showCustomRange && (
                <div className="custom-range-overlay" onClick={() => setShowCustomRange(false)}>
                    <div className="custom-range-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="custom-range-header">
                            <h3>Custom Range</h3>
                            <button className="close-btn" onClick={() => setShowCustomRange(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        
                        <div className="custom-range-body">
                            <div className="date-input-group">
                                <label>From Date</label>
                                <input
                                    type="date"
                                    value={customFromDate}
                                    onChange={(e) => setCustomFromDate(e.target.value)}
                                    className="date-input"
                                />
                            </div>
                            
                            <div className="date-input-group">
                                <label>To Date</label>
                                <input
                                    type="date"
                                    value={customToDate}
                                    onChange={(e) => setCustomToDate(e.target.value)}
                                    className="date-input"
                                />
                            </div>
                        </div>
                        
                        <div className="custom-range-footer">
                            <button 
                                className="cancel-btn" 
                                onClick={() => setShowCustomRange(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="apply-btn" 
                                onClick={handleCustomRangeApply}
                                disabled={!customFromDate || !customToDate}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Styles */}
            <style>{`
                .global-filter-container {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .filter-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                }

                .filter-trigger-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 16px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 14px;
                    color: #333;
                    transition: all 0.2s ease;
                    min-height: 40px;
                }

                .filter-trigger-btn:hover {
                    border-color: #e66d33;
                    box-shadow: 0 2px 8px rgba(230, 109, 51, 0.1);
                }

                .filter-trigger-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .filter-label {
                    font-weight: 500;
                    white-space: nowrap;
                }

                .chevron-icon {
                    transition: transform 0.2s ease;
                }

                .chevron-icon.rotate {
                    transform: rotate(180deg);
                }

                .filter-dropdown-menu {
                    position: absolute;
                    top: calc(100% + 8px);
                    left: 0;
                    min-width: 200px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
                    z-index: 1000;
                    padding: 6px;
                    animation: dropdownSlide 0.2s ease;
                }

                @keyframes dropdownSlide {
                    from {
                        opacity: 0;
                        transform: translateY(-8px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .dropdown-item {
                    display: flex;
                    align-items: center;
                    width: 100%;
                    padding: 10px 14px;
                    border: none;
                    background: transparent;
                    cursor: pointer;
                    font-size: 14px;
                    color: #333;
                    border-radius: 8px;
                    transition: all 0.15s ease;
                }

                .dropdown-item:hover {
                    background: #f7f7f7;
                }

                .dropdown-item.active {
                    background: #e66d33;
                    color: white;
                }

                .dropdown-item.active:hover {
                    background: #d45a2a;
                }

                .dropdown-divider {
                    height: 1px;
                    background: #e2e8f0;
                    margin: 6px 8px;
                }

                .clear-btn {
                    color: #ef4444;
                }

                .clear-btn:hover {
                    background: #fef2f2;
                }

                .export-btn {
                    padding: 8px 20px;
                    background: #e66d33;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    min-height: 40px;
                }

                .export-btn:hover {
                    background: #d45a2a;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(230, 109, 51, 0.3);
                }

                /* Custom Range Modal */
                .custom-range-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    // background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                    animation: fadeIn 0.2s ease;
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }

                .custom-range-modal {
                    background: white;
                    border-radius: 16px;
                    width: 420px;
                    max-width: 90%;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                    animation: slideUp 0.3s ease;
                }

                @keyframes slideUp {
                    from {
                        transform: translateY(20px) scale(0.95);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0) scale(1);
                        opacity: 1;
                    }
                }

                .custom-range-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 18px 24px;
                    border-bottom: 1px solid #f0f0f0;
                }

                .custom-range-header h3 {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 600;
                    color: #333;
                }

                .close-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 4px;
                    color: #999;
                    transition: color 0.2s ease;
                }

                .close-btn:hover {
                    color: #333;
                }

                .custom-range-body {
                    padding: 24px;
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .date-input-group {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .date-input-group label {
                    font-size: 14px;
                    font-weight: 500;
                    color: #555;
                }

                .date-input {
                    padding: 10px 12px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 14px;
                    outline: none;
                    transition: border-color 0.2s ease;
                }

                .date-input:focus {
                    border-color: #e66d33;
                    box-shadow: 0 0 0 3px rgba(230, 109, 51, 0.1);
                }

                .custom-range-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 10px;
                    padding: 16px 24px;
                    border-top: 1px solid #f0f0f0;
                }

                .cancel-btn {
                    padding: 8px 20px;
                    background: #f0f0f0;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    cursor: pointer;
                    transition: background 0.2s ease;
                }

                .cancel-btn:hover {
                    background: #e0e0e0;
                }

                .apply-btn {
                    padding: 8px 24px;
                    background: #e66d33;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .apply-btn:hover {
                    background: #d45a2a;
                    transform: translateY(-1px);
                }

                .apply-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    transform: none;
                }

                /* Mobile Responsive */
                @media (max-width: 768px) {
                    .filter-wrapper {
                        flex-direction: column;
                        width: 100%;
                    }

                    .filter-trigger-btn {
                        width: 100%;
                        justify-content: center;
                    }

                    .export-btn {
                        width: 100%;
                        justify-content: center;
                    }

                    .filter-dropdown-menu {
                        position: fixed;
                        top: auto;
                        bottom: 0;
                        left: 0;
                        right: 0;
                        width: 100%;
                        border-radius: 16px 16px 0 0;
                        max-height: 60vh;
                        overflow-y: auto;
                        animation: slideUp 0.3s ease;
                    }

                    .custom-range-modal {
                        width: 95%;
                        max-height: 90vh;
                        overflow-y: auto;
                    }
                }
            `}</style>
        </div>
    );
};

export default GlobalFilter;